/**
 * Hook for accessing the current user's plan/entitlement.
 *
 * Uses TanStack Query so the Plan tab and any gated UI share a single,
 * cached source of truth that can be refreshed after a redemption.
 *
 * @module useCurrentPlan
 */

import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getCurrentPlan, type CurrentPlan } from '@/lib/planApi';
import { useAuth } from '@/hooks/useAuth';

export const CURRENT_PLAN_QUERY_KEY = ['currentPlan'] as const;

export const currentPlanQueryKey = (userId: string | null | undefined) =>
  userId ? ([...CURRENT_PLAN_QUERY_KEY, userId] as const) : CURRENT_PLAN_QUERY_KEY;

export interface UseCurrentPlanResult {
  plan: CurrentPlan | null;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
}

export function useCurrentPlan(enabled: boolean = true): UseCurrentPlanResult {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const { data, isLoading, isError, refetch } = useQuery<CurrentPlan>({
    queryKey: currentPlanQueryKey(userId),
    queryFn: getCurrentPlan,
    // Plan quota changes server-side after every simulation, so treat cached
    // data as immediately stale.  This ensures a background refetch happens
    // whenever the hook remounts (e.g. user navigates back to the Dashboard)
    // without causing a visible loading flash (stale data is still shown while
    // the new response is in flight).
    staleTime: 0,
    // Re-fetch when the browser window / tab regains focus so that switching
    // back from a simulation in another tab also shows fresh counts.
    refetchOnWindowFocus: true,
    retry: 1,
    enabled: enabled && Boolean(userId),
  });

  useEffect(() => {
    const expiresAt = data?.expires_at;
    if (!enabled || !expiresAt) return;
    const expiresAtMs = new Date(expiresAt).getTime();
    if (!Number.isFinite(expiresAtMs)) return;

    let timer: ReturnType<typeof setTimeout> | undefined;
    let cancelled = false;
    const maxTimerDelay = 2_147_000_000;

    const schedule = () => {
      if (cancelled) return;
      const remaining = expiresAtMs - Date.now();
      if (remaining <= 0) {
        void refetch().finally(() => {
          if (!cancelled) {
            timer = setTimeout(schedule, 60_000);
          }
        });
        return;
      }
      timer = setTimeout(schedule, Math.min(remaining + 250, maxTimerDelay));
    };

    schedule();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [data?.expires_at, enabled, refetch]);

  return {
    plan: data ?? null,
    isLoading,
    isError,
    refetch,
  };
}
